import { Component, OnInit, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LabourMobilizationService, LabourMobilizationPlan } from '../../shared/services/labour-mobilization.service';
import { ToastrMsgService } from '../../shared/services/shared-services/toastr.service';
import { ConfirmDeleteDialogComponent } from '../../shared/confirm-delete-dialog/confirm-delete-dialog.component';
import { ApiService } from '../../shared/services/api.service';

@Component({
  selector: 'app-mobilization-plans-list',
  templateUrl: './mobilization-plans-list.component.html',
  styleUrls: ['./mobilization-plans-list.component.css']
})
export class MobilizationPlansListComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  private destroy$ = new Subject<void>();

  displayedColumns: string[] = ['srNo', 'plan_date', 'project_name', 'carpenders', 'rein_fitters', 'masons', 'total_available', 'expected_total_strength', 'shortage_against_committed', 'net_increase', 'action'];
  dataSource = new MatTableDataSource<LabourMobilizationPlan>([]);
  searchKeyword = '';
  selectedProject = '';
  startDate = '';
  endDate = '';
  projects: any[] = [];
  loading = false;
  pagination = { current_page: 1, total_pages: 1, total_items: 0, items_per_page: 10 };

  constructor(
    private labourService: LabourMobilizationService,
    private apiService: ApiService,
    private toastrMsgService: ToastrMsgService,
    private dialog: MatDialog,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadProjects();
    this.fetchData();
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    if (this.paginator) {
      this.paginator.page.subscribe(() => {
        this.pagination.current_page = this.paginator.pageIndex + 1;
        this.pagination.items_per_page = this.paginator.pageSize;
        this.fetchData();
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadProjects(): void {
    this.apiService.getProjectCommonAll({ limit: 1000 }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => this.projects = res.data || res || [],
      error: (err) => console.error('Error loading projects:', err)
    });
  }

  fetchData(): void {
    this.loading = true;
    const params: any = { page: this.pagination.current_page, limit: this.pagination.items_per_page };
    if (this.searchKeyword) params.search = this.searchKeyword;
    if (this.selectedProject) params.project_id = this.selectedProject;
    if (this.startDate) params.start_date = this.startDate;
    if (this.endDate) params.end_date = this.endDate;

    this.labourService.getMobilizationPlans(params).pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        this.dataSource.data = response.data || [];
        if (response.pagination) this.pagination = response.pagination;
        this.loading = false;
      },
      error: (err) => {
        console.error('Error fetching plans:', err);
        this.toastrMsgService.error(err.error?.message || 'Failed to load plans');
        this.loading = false;
      }
    });
  }

  applyFilter(): void {
    this.pagination.current_page = 1;
    if (this.paginator) this.paginator.pageIndex = 0;
    this.fetchData();
  }

  onFilterChange(): void { this.applyFilter(); }

  viewPlan(plan: LabourMobilizationPlan): void {
    this.router.navigate(['/home/labour-mobilization/mobilization-plans', plan.id]);
  }

  editPlan(plan: LabourMobilizationPlan): void {
    this.router.navigate(['/home/labour-mobilization/mobilization-plans/edit', plan.id]);
  }

  deletePlan(plan: LabourMobilizationPlan): void {
    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '400px',
      data: { itemName: `Plan for ${plan.project_name} on ${this.formatDate(plan.plan_date)}` }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result && plan.id) {
        this.labourService.deleteMobilizationPlan(plan.id).pipe(takeUntil(this.destroy$)).subscribe({
          next: () => {
            this.toastrMsgService.success('Plan deleted successfully');
            this.fetchData();
          },
          error: (err) => {
            this.toastrMsgService.error(err.error?.message || 'Failed to delete plan');
          }
        });
      }
    });
  }

  addNewPlan(): void {
    this.router.navigate(['/home/labour-mobilization/mobilization-plans/add']);
  }

  formatDate(dateString: string): string {
    return this.labourService.formatDate(dateString);
  }
}

